import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { ThemeMode, ThemeState, TenantThemeModeOverrides, TenantThemePayload, TenantThemeTokenOverrideMap } from './theme.model';
import { tenantThemeOverrideVariables } from './tenant-theme-overrides.constants';

@Injectable({
  providedIn: 'root'
})
export class ThemeService {
  private readonly modeStorageKey = 'app.theme.mode';
  private readonly tenantStorageKey = 'app.theme.tenant.id';
  private readonly tenantOverrideStyleElementId = 'tenant-theme-overrides';
  private readonly primaryPropertyName = '--tenant-primary';
  private readonly secondaryPropertyName = '--tenant-secondary';
  private readonly logoUrlPropertyName = '--tenant-logo-url';
  private readonly logoUrlDarkModePropertyName = '--tenant-logo-url-dark';
  private readonly defaultLogoUrl = 'assets/img/homelogo.png';
  private readonly defaultLogoUrlDarkMode = 'assets/img/logosmall-white.png';
  private readonly modeSubject: BehaviorSubject<ThemeMode>;
  public readonly mode$: Observable<ThemeMode>;
  private readonly logoUrlSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);
  public readonly logoUrl$: Observable<string | null> = this.logoUrlSubject.asObservable();
  private currentLogoUrl: string | null = null;
  private currentLogoUrlDarkMode: string | null = null;

  private readonly colorPattern = /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/;
  private readonly cssVariablePattern = /^--[a-zA-Z0-9-_]+$/;
  private readonly cssValuePattern = /^[#(),.%\-\s\w"':/]+$/;
  private readonly allowedOverrideVariables: Set<string> = new Set(tenantThemeOverrideVariables);

  constructor(@Inject(DOCUMENT) private readonly document: Document) {
    this.modeSubject = new BehaviorSubject<ThemeMode>(this.resolveInitialMode());
    this.mode$ = this.modeSubject.asObservable();
  }

  public initialize(): void {
    const initialMode = this.resolveInitialMode();
    this.setMode(initialMode);
  }

  public getCurrentMode(): ThemeMode {
    return this.modeSubject.value;
  }

  public setMode(mode: ThemeMode): void {
    const resolvedMode = this.isThemeMode(mode) ? mode : 'light';
    this.document.documentElement.setAttribute('data-theme', resolvedMode);
    this.document.documentElement.setAttribute("data-bs-theme", resolvedMode);

    if (resolvedMode === 'light') {
      document.body.classList.remove("dark");
      document.body.classList.remove("e-dark-mode");
    } else {
      document.body.classList.add("e-dark-mode");
      document.body.classList.add("dark");
    }

    localStorage.setItem(this.modeStorageKey, resolvedMode);
    this.modeSubject.next(resolvedMode);
    this.updateResolvedLogoUrl();
  }

  public applyTenantTheme(tenantId: string, payload: TenantThemePayload | null): void {
    const rootElement = this.document.documentElement;

    if (!payload) {
      rootElement.removeAttribute('data-tenant-id');
      rootElement.style.removeProperty(this.primaryPropertyName);
      rootElement.style.removeProperty(this.secondaryPropertyName);
      rootElement.style.removeProperty(this.logoUrlPropertyName);
      rootElement.style.removeProperty(this.logoUrlDarkModePropertyName);
      this.clearTenantThemeOverrides();
      localStorage.removeItem(this.tenantStorageKey);
      this.currentLogoUrl = null;
      this.currentLogoUrlDarkMode = null;
      this.logoUrlSubject.next(null);
      return;
    }

    if (payload.primary && this.isColorValue(payload.primary)) {
      rootElement.style.setProperty(this.primaryPropertyName, payload.primary);
    } else {
      rootElement.style.removeProperty(this.primaryPropertyName);
    }

    if (payload.secondary && this.isColorValue(payload.secondary)) {
      rootElement.style.setProperty(this.secondaryPropertyName, payload.secondary);
    } else {
      rootElement.style.removeProperty(this.secondaryPropertyName);
    }

    this.currentLogoUrl = payload.logoUrl || null;
    this.currentLogoUrlDarkMode = payload.logoUrlDarkMode || null;

    if (this.currentLogoUrlDarkMode) {
      rootElement.style.setProperty(this.logoUrlDarkModePropertyName, `url("${this.currentLogoUrlDarkMode}")`);
    } else {
      rootElement.style.removeProperty(this.logoUrlDarkModePropertyName);
    }

    this.updateResolvedLogoUrl();

    this.applyTenantThemeOverrides(payload.overrides);

    rootElement.setAttribute('data-tenant-id', tenantId);
    localStorage.setItem(this.tenantStorageKey, tenantId);
  }

  public applyTenantThemeOverridesPreview(overrides: TenantThemeModeOverrides | null | undefined): void {
    this.applyTenantThemeOverrides(overrides);
  }

  public getThemeState(): ThemeState {
    return {
      mode: this.getCurrentMode(),
      tenantId: localStorage.getItem(this.tenantStorageKey)
    };
  }

  public getCurrentLogoUrl(): string | null {
    return this.currentLogoUrl;
  }

  public getCurrentLogoUrlDarkMode(): string | null {
    return this.currentLogoUrlDarkMode;
  }

  /**
   * Returns the default (non-tenant) application logo for the given theme mode,
   * falling back to the current mode when none is provided.
   */
  public getDefaultLogoUrl(mode: ThemeMode = this.getCurrentMode()): string {
    return mode === 'dark' ? this.defaultLogoUrlDarkMode : this.defaultLogoUrl;
  }

  private resolveInitialMode(): ThemeMode {
    const storedMode = localStorage.getItem(this.modeStorageKey);
    if (this.isThemeMode(storedMode)) {
      return storedMode;
    }

    return this.prefersDarkScheme() ? 'dark' : 'light';
  }

  private prefersDarkScheme(): boolean {
    return typeof window !== 'undefined'
      && typeof window.matchMedia === 'function'
      && window.matchMedia('(prefers-color-scheme: dark)').matches;
  }

  private isThemeMode(mode: unknown): mode is ThemeMode {
    return mode === 'light' || mode === 'dark';
  }

  private isColorValue(value: string): boolean {
    return this.colorPattern.test(value);
  }

  /**
   * Resolves the effective logo URL for the current theme mode: uses the dark-mode logo
   * when in dark mode and one is specified, otherwise falls back to the regular tenant
   * logo (the caller falls back to the default application logo when both are unset).
   */
  private resolveLogoUrlForMode(): string | null {
    if (this.getCurrentMode() === 'dark' && this.currentLogoUrlDarkMode) {
      return this.currentLogoUrlDarkMode;
    }

    return this.currentLogoUrl;
  }

  private updateResolvedLogoUrl(): void {
    const rootElement = this.document.documentElement;
    const resolvedLogoUrl = this.resolveLogoUrlForMode();

    if (resolvedLogoUrl) {
      rootElement.style.setProperty(this.logoUrlPropertyName, `url("${resolvedLogoUrl}")`);
    } else {
      rootElement.style.removeProperty(this.logoUrlPropertyName);
    }

    this.logoUrlSubject.next(resolvedLogoUrl);
  }

  private applyTenantThemeOverrides(overrides: TenantThemeModeOverrides | null | undefined): void {
    const css = this.buildTenantOverridesCss(overrides);

    if (!css) {
      this.clearTenantThemeOverrides();
      return;
    }

    const styleElement = this.ensureTenantOverrideStyleElement();
    styleElement.textContent = css;
  }

  private buildTenantOverridesCss(overrides: TenantThemeModeOverrides | null | undefined): string {
    if (!overrides) {
      return '';
    }

    const lightCssVariables = this.toCssVariableDeclarations(overrides.light);
    const darkCssVariables = this.toCssVariableDeclarations(overrides.dark);

    const rules: string[] = [];

    if (lightCssVariables) {
      rules.push(`[data-theme='light'] { ${lightCssVariables} }`);
    }

    if (darkCssVariables) {
      rules.push(`[data-theme='dark'] { ${darkCssVariables} }`);
    }

    return rules.join('\n');
  }

  private toCssVariableDeclarations(source: TenantThemeTokenOverrideMap | null | undefined): string {
    if (!source) {
      return '';
    }

    const declarations = Object.entries(source)
      .map(([key, value]) => {
        const cssVariableName = this.sanitizeCssVariableName(key);
        const cssVariableValue = this.sanitizeCssVariableValue(value);

        if (!cssVariableName || !cssVariableValue) {
          return '';
        }

        return `${cssVariableName}: ${cssVariableValue};`;
      })
      .filter(Boolean);

    return declarations.join(' ');
  }

  private sanitizeCssVariableName(value: string): string {
    const normalizedValue = String(value ?? '').trim();
    if (!normalizedValue || !this.cssVariablePattern.test(normalizedValue)) {
      return '';
    }

    return this.allowedOverrideVariables.has(normalizedValue) ? normalizedValue : '';
  }

  private sanitizeCssVariableValue(value: string): string {
    const normalizedValue = String(value ?? '').trim();
    if (!normalizedValue) {
      return '';
    }

    if (normalizedValue.length > 96) {
      return '';
    }

    return this.cssValuePattern.test(normalizedValue) ? normalizedValue : '';
  }

  private ensureTenantOverrideStyleElement(): HTMLStyleElement {
    const existingElement = this.document.getElementById(this.tenantOverrideStyleElementId);
    if (existingElement instanceof HTMLStyleElement) {
      return existingElement;
    }

    const styleElement = this.document.createElement('style');
    styleElement.id = this.tenantOverrideStyleElementId;
    this.document.head.appendChild(styleElement);
    return styleElement;
  }

  private clearTenantThemeOverrides(): void {
    const styleElement = this.document.getElementById(this.tenantOverrideStyleElementId);
    styleElement?.remove();
  }
}
